import { createSlice } from "@reduxjs/toolkit";
import { IGenerateNews, INews, ITossStock } from "api/types/_news";
import { PaginationResponse, ResponseData } from "api/types/_public";
import {
  getGenerateNews, getListNews, getNewsV2s, getNewsKeywords, getNewsV2Logs,
  getNewsTravels, getNewsTravelLogs, getPlaceTravels, getNewsStocks, getNewsStockLogs,
  getTossStocks, getNewsTossStocks, getNewsTossStockLogs, getNewsStockSchedules,
  getPlaceWeathers, getNewsWeathers, getNewsWeatherLogs,
  getNewsWeatherWarnings,
  getNewsWeatherWarningLogs,
  getNewsBoostingLogs
} from "./thunk";


export interface IState {
  news: ResponseData<INews[]> & PaginationResponse | null,
  isNewsLoading: boolean,
  isNewsSuccess: boolean,

  newsV2s: ResponseData<INews[]> & PaginationResponse | null,
  isNewsV2Loading: boolean,
  isNewsV2Success: boolean,

  newsV2Logs: ResponseData<INews[]> & PaginationResponse | null,
  isNewsV2LogLoading: boolean,
  isNewsV2LogSuccess: boolean,

  newsBoostingLogs: ResponseData<INews[]> & PaginationResponse | null,
  isNewsBoostingLogLoading: boolean,
  isNewsBoostingLogSuccess: boolean,

  newsTravels: ResponseData<INews[]> & PaginationResponse | null,
  isNewsTravelLoading: boolean,
  isNewsTravelSuccess: boolean,

  placeTravels: ResponseData<INews[]> & PaginationResponse | null,
  isPlaceTravelLoading: boolean,
  isPlaceTravelSuccess: boolean,

  newsTravelLogs: ResponseData<INews[]> & PaginationResponse | null,
  isNewsTravelLogLoading: boolean,
  isNewsTravelLogSuccess: boolean,

  placeWeathers: ResponseData<INews[]> & PaginationResponse | null,
  isPlaceWeatherLoading: boolean,
  isPlaceWeatherSuccess: boolean,

  newsWeathers: ResponseData<INews[]> & PaginationResponse | null,
  isNewsWeatherLoading: boolean,
  isNewsWeatherSuccess: boolean,

  newsWeatherLogs: ResponseData<INews[]> & PaginationResponse | null,
  isNewsWeatherLogLoading: boolean,
  isNewsWeatherLogSuccess: boolean,

  newsWeatherWarnings: ResponseData<INews[]> & PaginationResponse | null,
  isNewsWeatherWarningLoading: boolean,
  isNewsWeatherWarningSuccess: boolean,

  newsWeatherWarningLogs: ResponseData<INews[]> & PaginationResponse | null,
  isNewsWeatherWarningLogLoading: boolean,
  isNewsWeatherWarningLogSuccess: boolean,

  newsStocks: ResponseData<INews[]> & PaginationResponse | null,
  isNewsStockLoading: boolean,
  isNewsStockSuccess: boolean,

  newsStockLogs: ResponseData<INews[]> & PaginationResponse | null,
  isNewsStockLogLoading: boolean,
  isNewsStockLogSuccess: boolean,

  newsStockSchedules: ResponseData<INews[]> & PaginationResponse | null,
  isNewsStockScheduleLoading: boolean,
  isNewsStockScheduleSuccess: boolean,

  tossStocks: ResponseData<ITossStock[]> & PaginationResponse | null,
  isTossStockLoading: boolean,
  isTossStockSuccess: boolean,

  newsTossStocks: ResponseData<INews[]> & PaginationResponse | null,
  isNewsTossStockLoading: boolean,
  isNewsTossStockSuccess: boolean,

  newsTossStockLogs: ResponseData<INews[]> & PaginationResponse | null,
  isNewsTossStockLogLoading: boolean,
  isNewsTossStockLogSuccess: boolean,

  keywords: ResponseData<INews[]> & PaginationResponse | null,
  isKeywordLoading: boolean,
  isKeywordSuccess: boolean,

  generateNews: ResponseData<IGenerateNews[]> & PaginationResponse | null,
  isGenerateNewsLoading: boolean,
  isGenerateNewsSuccess: boolean,

  error: any,
};

export const initialState: IState = {
  news: null,
  isNewsLoading: false,
  isNewsSuccess: false,

  newsV2s: null,
  isNewsV2Loading: false,
  isNewsV2Success: false,

  newsV2Logs: null,
  isNewsV2LogLoading: false,
  isNewsV2LogSuccess: false,

  newsBoostingLogs: null,
  isNewsBoostingLogLoading: false,
  isNewsBoostingLogSuccess: false,

  newsTravels: null,
  isNewsTravelLoading: false,
  isNewsTravelSuccess: false,

  placeTravels: null,
  isPlaceTravelLoading: false,
  isPlaceTravelSuccess: false,

  newsTravelLogs: null,
  isNewsTravelLogLoading: false,
  isNewsTravelLogSuccess: false,

  placeWeathers: null,
  isPlaceWeatherLoading: false,
  isPlaceWeatherSuccess: false,

  newsWeathers: null,
  isNewsWeatherLoading: false,
  isNewsWeatherSuccess: false,

  newsWeatherLogs: null,
  isNewsWeatherLogLoading: false,
  isNewsWeatherLogSuccess: false,

  newsWeatherWarnings: null,
  isNewsWeatherWarningLoading: false,
  isNewsWeatherWarningSuccess: false,

  newsWeatherWarningLogs: null,
  isNewsWeatherWarningLogLoading: false,
  isNewsWeatherWarningLogSuccess: false,

  newsStocks: null,
  isNewsStockLoading: false,
  isNewsStockSuccess: false,

  newsStockLogs: null,
  isNewsStockLogLoading: false,
  isNewsStockLogSuccess: false,

  newsStockSchedules: null,
  isNewsStockScheduleLoading: false,
  isNewsStockScheduleSuccess: false,

  tossStocks: null,
  isTossStockLoading: false,
  isTossStockSuccess: false,

  newsTossStocks: null,
  isNewsTossStockLoading: false,
  isNewsTossStockSuccess: false,

  newsTossStockLogs: null,
  isNewsTossStockLogLoading: false,
  isNewsTossStockLogSuccess: false,

  keywords: null,
  isKeywordLoading: false,
  isKeywordSuccess: false,

  generateNews: null,
  isGenerateNewsLoading: false,
  isGenerateNewsSuccess: false,

  error: {},
};

const NewsSlice = createSlice({
  name: "News",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    //get News
    builder.addCase(getListNews.pending, (state: IState, action: any) => {
      state.isNewsLoading = true
    });
    builder.addCase(getListNews.fulfilled, (state: IState, action: any) => {
      state.news = action.payload.data;
      state.isNewsSuccess = true;
      state.isNewsLoading = false;
    });
    builder.addCase(getListNews.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsSuccess = false;
      state.isNewsLoading = false;
    });

    //get List News V2s
    builder.addCase(getNewsV2s.pending, (state: IState, action: any) => {
      state.isNewsV2Loading = true
    });
    builder.addCase(getNewsV2s.fulfilled, (state: IState, action: any) => {
      state.newsV2s = action.payload.data;
      state.isNewsV2Success = true;
      state.isNewsV2Loading = false;
    });
    builder.addCase(getNewsV2s.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsV2Success = false;
      state.isNewsV2Loading = false;
    });

    //get List News Stock Schedules
    builder.addCase(getNewsStockSchedules.pending, (state: IState, action: any) => {
      state.isNewsStockScheduleLoading = true
    });
    builder.addCase(getNewsStockSchedules.fulfilled, (state: IState, action: any) => {
      state.newsStockSchedules = action.payload.data;
      state.isNewsStockScheduleSuccess = true;
      state.isNewsStockScheduleLoading = false;
    });
    builder.addCase(getNewsStockSchedules.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsStockScheduleSuccess = false;
      state.isNewsStockScheduleLoading = false;
    });

    //get List News V2 Logs
    builder.addCase(getNewsV2Logs.pending, (state: IState, action: any) => {
      state.isNewsV2LogLoading = true
    });
    builder.addCase(getNewsV2Logs.fulfilled, (state: IState, action: any) => {
      state.newsV2Logs = action.payload.data;
      state.isNewsV2LogSuccess = true;
      state.isNewsV2LogLoading = false;
    });
    builder.addCase(getNewsV2Logs.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsV2LogSuccess = false;
      state.isNewsV2LogLoading = false;
    });

    //get List News Boosting Logs
    builder.addCase(getNewsBoostingLogs.pending, (state: IState, action: any) => {
      state.isNewsBoostingLogLoading = true
    });
    builder.addCase(getNewsBoostingLogs.fulfilled, (state: IState, action: any) => {
      state.newsBoostingLogs = action.payload.data;
      state.isNewsBoostingLogSuccess = true;
      state.isNewsBoostingLogLoading = false;
    });
    builder.addCase(getNewsBoostingLogs.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsBoostingLogSuccess = false;
      state.isNewsBoostingLogLoading = false;
    });


    //get List News Travel
    builder.addCase(getNewsTravels.pending, (state: IState, action: any) => {
      state.isNewsTravelLoading = true
    });
    builder.addCase(getNewsTravels.fulfilled, (state: IState, action: any) => {
      state.newsTravels = action.payload.data;
      state.isNewsTravelSuccess = true;
      state.isNewsTravelLoading = false;
    });
    builder.addCase(getNewsTravels.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsTravelSuccess = false;
      state.isNewsTravelLoading = false;
    });

    //get List Place Travel
    builder.addCase(getPlaceTravels.pending, (state: IState, action: any) => {
      state.isPlaceTravelLoading = true
    });
    builder.addCase(getPlaceTravels.fulfilled, (state: IState, action: any) => {
      state.placeTravels = action.payload.data;
      state.isPlaceTravelSuccess = true;
      state.isPlaceTravelLoading = false;
    });
    builder.addCase(getPlaceTravels.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isPlaceTravelSuccess = false;
      state.isPlaceTravelLoading = false;
    });


    //get List News Travel Logs
    builder.addCase(getNewsTravelLogs.pending, (state: IState, action: any) => {
      state.isNewsTravelLogLoading = true
    });
    builder.addCase(getNewsTravelLogs.fulfilled, (state: IState, action: any) => {
      state.newsTravelLogs = action.payload.data;
      state.isNewsTravelLogSuccess = true;
      state.isNewsTravelLogLoading = false;
    });
    builder.addCase(getNewsTravelLogs.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsTravelLogSuccess = false;
      state.isNewsTravelLogLoading = false;
    });


    //get List Place Weather
    builder.addCase(getPlaceWeathers.pending, (state: IState, action: any) => {
      state.isPlaceWeatherLoading = true
    });
    builder.addCase(getPlaceWeathers.fulfilled, (state: IState, action: any) => {
      state.placeWeathers = action.payload.data;
      state.isPlaceWeatherSuccess = true;
      state.isPlaceWeatherLoading = false;
    });
    builder.addCase(getPlaceWeathers.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isPlaceWeatherSuccess = false;
      state.isPlaceWeatherLoading = false;
    });

    //get List News Weather Logs
    builder.addCase(getNewsWeatherLogs.pending, (state: IState, action: any) => {
      state.isNewsWeatherLogLoading = true
    });
    builder.addCase(getNewsWeatherLogs.fulfilled, (state: IState, action: any) => {
      state.newsWeatherLogs = action.payload.data;
      state.isNewsWeatherLogSuccess = true;
      state.isNewsWeatherLogLoading = false;
    });
    builder.addCase(getNewsWeatherLogs.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsWeatherLogSuccess = false;
      state.isNewsWeatherLogLoading = false;
    });

    //get List News Weather
    builder.addCase(getNewsWeathers.pending, (state: IState, action: any) => {
      state.isNewsWeatherLoading = true
    });
    builder.addCase(getNewsWeathers.fulfilled, (state: IState, action: any) => {
      state.newsWeathers = action.payload.data;
      state.isNewsWeatherSuccess = true;
      state.isNewsWeatherLoading = false;
    });
    builder.addCase(getNewsWeathers.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsWeatherSuccess = false;
      state.isNewsWeatherLoading = false;
    });

    //get List News Weather Warnings
    builder.addCase(getNewsWeatherWarnings.pending, (state: IState, action: any) => {
      state.isNewsWeatherWarningLoading = true
    });
    builder.addCase(getNewsWeatherWarnings.fulfilled, (state: IState, action: any) => {
      state.newsWeatherWarnings = action.payload.data;
      state.isNewsWeatherWarningSuccess = true;
      state.isNewsWeatherWarningLoading = false;
    });
    builder.addCase(getNewsWeatherWarnings.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsWeatherWarningSuccess = false;
      state.isNewsWeatherWarningLoading = false;
    });

    //get List News Weather Warning Logs
    builder.addCase(getNewsWeatherWarningLogs.pending, (state: IState, action: any) => {
      state.isNewsWeatherWarningLogLoading = true
    });
    builder.addCase(getNewsWeatherWarningLogs.fulfilled, (state: IState, action: any) => {
      state.newsWeatherWarningLogs = action.payload.data;
      state.isNewsWeatherWarningLogSuccess = true;
      state.isNewsWeatherWarningLogLoading = false;
    });
    builder.addCase(getNewsWeatherWarningLogs.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsWeatherWarningLogSuccess = false;
      state.isNewsWeatherWarningLogLoading = false;
    });

    //get List News Stocks
    builder.addCase(getNewsStocks.pending, (state: IState, action: any) => {
      state.isNewsStockLoading = true
    });
    builder.addCase(getNewsStocks.fulfilled, (state: IState, action: any) => {
      state.newsStocks = action.payload.data;
      state.isNewsStockSuccess = true;
      state.isNewsStockLoading = false;
    });
    builder.addCase(getNewsStocks.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsStockSuccess = false;
      state.isNewsStockLoading = false;
    });

    //get List News Stock Logs
    builder.addCase(getNewsStockLogs.pending, (state: IState, action: any) => {
      state.isNewsStockLogLoading = true
    });
    builder.addCase(getNewsStockLogs.fulfilled, (state: IState, action: any) => {
      state.newsStockLogs = action.payload.data;
      state.isNewsStockLogSuccess = true;
      state.isNewsStockLogLoading = false;
    });
    builder.addCase(getNewsStockLogs.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsStockLogSuccess = false;
      state.isNewsStockLogLoading = false;
    });


    //get List Toss Stocks
    builder.addCase(getTossStocks.pending, (state: IState, action: any) => {
      state.isTossStockLoading = true
    });
    builder.addCase(getTossStocks.fulfilled, (state: IState, action: any) => {
      state.tossStocks = action.payload.data;
      state.isTossStockSuccess = true;
      state.isTossStockLoading = false;
    });
    builder.addCase(getTossStocks.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isTossStockSuccess = false;
      state.isTossStockLoading = false;
    });

    //get List News Toss Stocks
    builder.addCase(getNewsTossStocks.pending, (state: IState, action: any) => {
      state.isNewsTossStockLoading = true
    });
    builder.addCase(getNewsTossStocks.fulfilled, (state: IState, action: any) => {
      state.newsTossStocks = action.payload.data;
      state.isNewsTossStockSuccess = true;
      state.isNewsTossStockLoading = false;
    });
    builder.addCase(getNewsTossStocks.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsTossStockSuccess = false;
      state.isNewsTossStockLoading = false;
    });

    //get List News Toss Stock Logs
    builder.addCase(getNewsTossStockLogs.pending, (state: IState, action: any) => {
      state.isNewsTossStockLogLoading = true
    });
    builder.addCase(getNewsTossStockLogs.fulfilled, (state: IState, action: any) => {
      state.newsTossStockLogs = action.payload.data;
      state.isNewsTossStockLogSuccess = true;
      state.isNewsTossStockLogLoading = false;
    });
    builder.addCase(getNewsTossStockLogs.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isNewsTossStockLogSuccess = false;
      state.isNewsTossStockLogLoading = false;
    });

    //get Keywords
    builder.addCase(getNewsKeywords.pending, (state: IState, action: any) => {
      state.isKeywordLoading = true
    });
    builder.addCase(getNewsKeywords.fulfilled, (state: IState, action: any) => {
      state.keywords = action.payload.data;
      state.isKeywordSuccess = true;
      state.isKeywordLoading = false;
    });
    builder.addCase(getNewsKeywords.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isKeywordSuccess = false;
      state.isKeywordLoading = false;
    });

    //get Generate News
    builder.addCase(getGenerateNews.pending, (state: IState, action: any) => {
      state.isGenerateNewsLoading = true
    });
    builder.addCase(getGenerateNews.fulfilled, (state: IState, action: any) => {
      state.generateNews = action.payload.data;
      state.isGenerateNewsSuccess = true;
      state.isGenerateNewsLoading = false;
    });
    builder.addCase(getGenerateNews.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isGenerateNewsSuccess = false;
      state.isGenerateNewsLoading = false;
    });
  },
});

export default NewsSlice.reducer;